import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Scale, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Educational modal explaining what BMI is and why it's used in weight management.
 * Visual style mirrors the Fact-or-Myth modal: purple/indigo gradient backdrop,
 * animated radial blobs, floating dots, white center card.
 *
 * Respects `prefers-reduced-motion` — when the OS / browser reports motion
 * sensitivity, all decorative blob & dot animations are flattened to static
 * positions. The modal itself still fades in for clarity but doesn't move.
 *
 * Props:
 *   - open: boolean — controls visibility
 *   - onClose: () => void
 */
export default function BmiInfoModal({ open, onClose }) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 overflow-hidden"
          onClick={onClose}
          data-testid="bmi-info-modal"
        >
          {/* Animated science backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/85 via-slate-900/85 to-indigo-900/85 backdrop-blur-sm" aria-hidden />
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
            <motion.div
              className="absolute -left-40 -top-40 w-[28rem] h-[28rem] rounded-full opacity-40"
              style={{ background: "radial-gradient(circle, rgba(147,51,234,0.55), transparent 60%)" }}
              animate={prefersReducedMotion ? undefined : { x: [0, 40, -20, 0], y: [0, 20, -30, 0] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute -right-32 top-1/3 w-[24rem] h-[24rem] rounded-full opacity-40"
              style={{ background: "radial-gradient(circle, rgba(56,189,248,0.45), transparent 60%)" }}
              animate={prefersReducedMotion ? undefined : { x: [0, -30, 20, 0], y: [0, -20, 15, 0] }}
              transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute left-1/4 -bottom-40 w-[26rem] h-[26rem] rounded-full opacity-30"
              style={{ background: "radial-gradient(circle, rgba(244,114,182,0.45), transparent 60%)" }}
              animate={prefersReducedMotion ? undefined : { x: [0, 20, -10, 0], y: [0, -15, 10, 0] }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            {!prefersReducedMotion && Array.from({ length: 10 }).map((_, i) => (
              <motion.span
                key={`dot-${i}`}
                className="absolute rounded-full bg-white/40"
                style={{
                  left: `${(i * 73) % 100}%`,
                  top: `${(i * 37) % 100}%`,
                  width: 4 + (i % 3) * 2,
                  height: 4 + (i % 3) * 2,
                }}
                animate={{ opacity: [0.15, 0.6, 0.15], y: [0, -10, 0] }}
                transition={{ duration: 4 + (i % 5), repeat: Infinity, delay: (i % 6) * 0.4, ease: "easeInOut" }}
              />
            ))}
          </div>

          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Scale className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>What is BMI?</span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                data-testid="bmi-info-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              <b className="text-slate-900">BMI (Body Mass Index)</b> is a simple number calculated from your height and weight. It&apos;s been used by doctors for decades as a quick way to estimate whether your weight falls into a healthy range for your height.
            </p>

            <div className="bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 mb-4">
              <p className="text-[11px] uppercase tracking-wider font-bold text-purple-700 mb-1">The formula</p>
              <p className="text-sm text-slate-700 font-mono">BMI = weight (lbs) / height² (in) × 703</p>
            </div>

            <p className="text-sm font-semibold text-slate-900 mb-2">Why it matters in weight management</p>
            <ul className="space-y-2 text-sm text-slate-600 leading-relaxed mb-4">
              <li className="flex gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><b>Quick screening tool.</b> Clinicians use BMI as a starting point to talk about weight-related health risks like type 2 diabetes, heart disease, and sleep apnea.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><b>Tracks progress.</b> Watching your BMI shift over time can be motivating and helps your provider tune your treatment plan.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><b>Common reference point.</b> Most clinical research on weight loss medications references BMI ranges, so it&apos;s a shared language between you and your provider.</span>
              </li>
            </ul>

            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4">
              <p className="text-xs font-semibold text-amber-800 mb-1">Important to know</p>
              <p className="text-xs text-amber-800/90 leading-relaxed">
                BMI doesn&apos;t measure body fat directly and doesn&apos;t account for muscle mass, bone density, age, sex, or ethnicity. A muscular athlete and someone with low muscle mass can have the same BMI but very different health profiles. <b>Treat your BMI as one data point, not a verdict.</b> Your GLPFlo provider looks at the whole picture.
              </p>
            </div>

            <Button
              onClick={onClose}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full"
              data-testid="bmi-info-got-it"
            >
              Got it
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
